import React, { useState, useEffect } from 'react';
import { diffAPI } from '../services/api';
import { GitCompare, Plus, Minus, AlertTriangle, Calendar, ChevronDown, ChevronRight, Trash2, Database } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const Diff = () => {
  const [diffs, setDiffs] = useState([]);
  const [groupedDiffs, setGroupedDiffs] = useState({});
  const [periodTotals, setPeriodTotals] = useState({});
  const [iterableUpdates, setIterableUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedDiff, setSelectedDiff] = useState(null);
  const [viewMode, setViewMode] = useState('grouped'); // 'grouped', 'list', or 'iterable'
  const [expandedPeriods, setExpandedPeriods] = useState(new Set(['today', 'yesterday']));
  const [expandedUpdates, setExpandedUpdates] = useState(new Set());
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [diffToDelete, setDiffToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [success, setSuccess] = useState('');
  const limit = 20;

  useEffect(() => {
    if (viewMode === 'grouped') {
      fetchGroupedDiffs();
    } else if (viewMode === 'iterable') {
      fetchIterableDiffs();
    } else {
      fetchDiffs();
    }
  }, [page, viewMode]);

  const fetchDiffs = async () => {
    try {
      setLoading(true);
      const response = await diffAPI.getDiff(page, limit);
      setDiffs(response.data.diffs);
      setTotal(response.data.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить различия');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupedDiffs = async () => {
    try {
      setLoading(true);
      const response = await diffAPI.getDiffGrouped();
      setGroupedDiffs(response.data.grouped_diffs);
      setPeriodTotals(response.data.period_totals);
      setTotal(response.data.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить сгруппированные различия');
    } finally {
      setLoading(false);
    }
  };

  const fetchIterableDiffs = async () => {
    try {
      setLoading(true);
      const response = await diffAPI.getDiffIterable();
      setIterableUpdates(response.data.iterable_updates);
      setTotal(response.data.total_diffs);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить итеративные различия');
    } finally {
      setLoading(false);
    }
  };

  const getChangeType = (changes) => {
    if (changes.new_host) return { type: 'new', label: 'Новый хост', color: '#10b981', icon: Plus };
    if (changes.removed_host) return { type: 'removed', label: 'Удалённый хост', color: '#ef4444', icon: Minus };
    if (changes.status_changed || changes.status_recovered) return { type: 'status', label: 'Статус изменён', color: '#3b82f6', icon: AlertTriangle };
    if (changes.host_changed) return { type: 'modified', label: 'Хост изменён', color: '#f59e0b', icon: AlertTriangle };
    if (changes.tech_changed || changes.links_changed || changes.modify_changed || changes.relations_changed) {
      return { type: 'modified', label: 'Изменён', color: '#f59e0b', icon: AlertTriangle };
    }
    // Fallback: check if any changes field has truthy value (e.g. location changes)
    const hasAnyChange = Object.entries(changes).some(([k, v]) => k !== 'changes_summary' && v && v !== false);
    if (hasAnyChange) return { type: 'modified', label: 'Изменён', color: '#f59e0b', icon: AlertTriangle };
    return { type: 'unknown', label: 'Неизвестно', color: '#64748b', icon: GitCompare };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getPeriodLabel = (period) => {
    const labels = {
      'today': 'Сегодня',
      'yesterday': 'Вчера', 
      'this_week': 'На этой неделе',
      'this_month': 'В этом месяце',
      'older': 'Ранее'
    };
    return labels[period] || period;
  };

  const getPeriodColor = (period) => {
    const colors = {
      'today': '#10b981',
      'yesterday': '#3b82f6',
      'this_week': '#f59e0b',
      'this_month': '#8b5cf6',
      'older': '#64748b'
    };
    return colors[period] || '#64748b';
  };

  const togglePeriodExpansion = (period) => {
    const newExpanded = new Set(expandedPeriods);
    if (newExpanded.has(period)) {
      newExpanded.delete(period);
    } else {
      newExpanded.add(period);
    }
    setExpandedPeriods(newExpanded);
  };

  const toggleUpdateExpansion = (updateId) => {
    const newExpanded = new Set(expandedUpdates);
    if (newExpanded.has(updateId)) {
      newExpanded.delete(updateId);
    } else {
      newExpanded.add(updateId);
    }
    setExpandedUpdates(newExpanded);
  };

  const handleDeleteDiff = async (diffId) => {
    try {
      setDeleting(true);
      setError('');
      setSuccess('');
      
      await diffAPI.deleteDiff(diffId);
      setSuccess('Изменение успешно удалено');
      setShowDeleteDialog(false);
      setDiffToDelete(null);
      
      // Refresh the data
      if (viewMode === 'grouped') {
        fetchGroupedDiffs();
      } else if (viewMode === 'iterable') {
        fetchIterableDiffs();
      } else {
        fetchDiffs();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось удалить изменение');
    } finally {
      setDeleting(false);
    }
  };

  const handleClearAllDiffs = async () => {
    try {
      setDeleting(true);
      setError('');
      setSuccess('');
      
      const response = await diffAPI.clearAllDiffs();
      setSuccess(`Все изменения успешно очищены. Удалено: ${response.data.deleted_count}.`);
      setShowClearDialog(false);
      
      // Refresh the data
      if (viewMode === 'grouped') {
        fetchGroupedDiffs();
      } else if (viewMode === 'iterable') {
        fetchIterableDiffs();
      } else {
        fetchDiffs();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось очистить изменения');
    } finally {
      setDeleting(false);
    }
  };

  const confirmDeleteDiff = (diff) => {
    setDiffToDelete(diff);
    setShowDeleteDialog(true);
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && diffs.length === 0) {
    return (
      <div className="loading">
        <div>Загрузка различий...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px' }}>
      <div className="page-header">
        <div className="page-header-icon">
          <GitCompare size={24} />
        </div>
        <div className="page-header-text">
          <h1>Различия</h1>
          <p>Отслеживание изменений между сканированиями ({total})</p>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* View Mode Toggle */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          alignItems: 'center',
        }}>
          <h3 style={{
            fontSize: '15px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginRight: 'auto',
          }}>
            <Calendar size={18} />
            Режим просмотра
          </h3>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setViewMode('grouped')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: viewMode === 'grouped' ? '#3b82f6' : 'var(--bg-secondary)',
                color: viewMode === 'grouped' ? 'white' : 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              По времени
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: viewMode === 'list' ? '#3b82f6' : 'var(--bg-secondary)',
                color: viewMode === 'list' ? 'white' : 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Список
            </button>
            <button
              onClick={() => setViewMode('iterable')}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                backgroundColor: viewMode === 'iterable' ? '#3b82f6' : 'var(--bg-secondary)',
                color: viewMode === 'iterable' ? 'white' : 'var(--text-primary)',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Итеративный вид
            </button>
          </div>
          
          {total > 0 && (
            <button
              onClick={() => setShowClearDialog(true)}
              className="btn btn-danger"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <Trash2 size={16} />
              Очистить все различия
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Diffs List */}
        <div style={{ flex: 1 }}>
          <div className="card">
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '16px',
            }}>
              {viewMode === 'grouped' ? 'Изменения по периодам' : 
               viewMode === 'iterable' ? 'Обновления конфига' : 'Все изменения'}
            </h3>

            {viewMode === 'grouped' && (
              /* Grouped View */
              <div>
                {Object.entries(groupedDiffs).map(([period, periodDiffs]) => (
                  <div key={period} style={{ marginBottom: '16px' }}>
                    <div
                      onClick={() => togglePeriodExpansion(period)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px',
                        backgroundColor: 'var(--bg-tertiary)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        border: `2px solid ${getPeriodColor(period)}20`,
                      }}
                    >
                      {expandedPeriods.has(period) ? (
                        <ChevronDown size={16} color={getPeriodColor(period)} />
                      ) : (
                        <ChevronRight size={16} color={getPeriodColor(period)} />
                      )}
                      <span style={{
                        fontWeight: '600',
                        color: getPeriodColor(period),
                        fontSize: '16px',
                      }}>
                        {getPeriodLabel(period)}
                      </span>
                      <span style={{
                        backgroundColor: getPeriodColor(period),
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                      }}>
                        {periodTotals[period] || 0}
                      </span>
                    </div>
                    
                    {expandedPeriods.has(period) && periodDiffs.length > 0 && (
                      <div style={{ marginTop: '8px', marginLeft: '16px' }}>
                        {periodDiffs.map((diff) => {
                          const changeInfo = getChangeType(diff.changes);
                          const Icon = changeInfo.icon;
                          
                          return (
                            <div
                              key={diff._id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '8px 12px',
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '6px',
                                marginBottom: '4px',
                                border: '1px solid var(--border-color)',
                              }}
                            >
                              <Icon size={16} color={changeInfo.color} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: 'var(--text-primary)',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {diff.host}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: 'var(--text-secondary)',
                                  marginBottom: '2px',
                                }}>
                                  {formatDate(diff.detected_at)}
                                </div>
                                {diff.changes.changes_summary && (
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'var(--text-tertiary)',
                                    fontStyle: 'italic',
                                    whiteSpace: 'pre-line',
                                  }}>
                                    {Array.isArray(diff.changes.changes_summary) 
                                      ? diff.changes.changes_summary.join('\n')
                                      : diff.changes.changes_summary}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => confirmDeleteDiff(diff)}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#ef4444',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                                title="Удалить это изменение"
                              >
                                <Trash2 size={14} />
                              </button>
                              <span style={{
                                backgroundColor: `${changeInfo.color}20`,
                                color: changeInfo.color,
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '500',
                              }}>
                                {changeInfo.label}
                              </span>
                              <button
                                onClick={() => setSelectedDiff(diff)}
                                className="btn btn-primary"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                Просмотр
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {viewMode === 'list' && (
              /* List View */
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>URL</th>
                      <th>Тип изменения</th>
                      <th>Обнаружено</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffs.map((diff) => {
                      const changeInfo = getChangeType(diff.changes);
                      const Icon = changeInfo.icon;
                      
                      return (
                        <tr key={diff._id}>
                          <td>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}>
                              <Icon size={16} color={changeInfo.color} />
                              <span style={{
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {diff.host}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span style={{
                              backgroundColor: `${changeInfo.color}20`,
                              color: changeInfo.color,
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                            }}>
                              {changeInfo.label}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>
                              {formatDate(diff.detected_at)}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => setSelectedDiff(diff)}
                                className="btn btn-primary"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              >
                                Подробнее
                              </button>
                              <button
                                onClick={() => confirmDeleteDiff(diff)}
                                style={{
                                  background: 'none',
                                  border: '1px solid #ef4444',
                                  color: '#ef4444',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                                title="Удалить это изменение"
                              >
                                <Trash2 size={12} />
                                Удалить
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {viewMode === 'iterable' && (
              /* Iterable View */
              <div>
                {iterableUpdates.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: 'var(--text-secondary)',
                  }}>
                    Обновления конфига не найдены
                  </div>
                ) : (
                  iterableUpdates.map((update) => (
                    <div key={update.config_id} style={{ marginBottom: '16px' }}>
                      <div
                        onClick={() => toggleUpdateExpansion(update.config_id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '16px',
                          backgroundColor: 'var(--bg-tertiary)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          border: '2px solid #3b82f620',
                        }}
                      >
                        {expandedUpdates.has(update.config_id) ? (
                          <ChevronDown size={20} color="#3b82f6" />
                        ) : (
                          <ChevronRight size={20} color="#3b82f6" />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: 'var(--text-primary)',
                            marginBottom: '4px',
                          }}>
                            Обновление конфига #{update.config_id.slice(-8)}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: 'var(--text-secondary)',
                            marginBottom: '4px',
                          }}>
                            {formatDate(update.config_created_at)} • Created by {update.config_created_by}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: 'var(--text-tertiary)',
                          }}>
                            Hash: {update.config_hash.slice(0, 16)}... • 
                            {update.diffs.length > 0 && update.diffs[0].previous_config_id && (
                              <span>Сравнено с конфигом #{update.diffs[0].previous_config_id.slice(-8)}</span>
                            )}
                          </div>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}>
                          <span style={{
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}>
                            {update.diff_count} changes
                          </span>
                        </div>
                      </div>
                      
                      {expandedUpdates.has(update.config_id) && update.diffs.length > 0 && (
                        <div style={{ marginTop: '8px', marginLeft: '16px' }}>
                          {update.diffs.map((diff) => {
                            const changeInfo = getChangeType(diff.changes);
                            const Icon = changeInfo.icon;
                            
                            return (
                              <div
                                key={diff._id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '12px',
                                  backgroundColor: 'var(--bg-secondary)',
                                  borderRadius: '6px',
                                  marginBottom: '6px',
                                  border: '1px solid var(--border-color)',
                                }}
                              >
                                <Icon size={16} color={changeInfo.color} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: 'var(--text-primary)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {diff.host}
                                  </div>
                                  <div style={{
                                    fontSize: '12px',
                                    color: 'var(--text-secondary)',
                                    marginBottom: '2px',
                                  }}>
                                    {formatDate(diff.detected_at)}
                                  </div>
                                  {diff.changes.changes_summary && (
                                    <div style={{
                                      fontSize: '11px',
                                      color: 'var(--text-tertiary)',
                                      fontStyle: 'italic',
                                      whiteSpace: 'pre-line',
                                    }}>
                                      {Array.isArray(diff.changes.changes_summary) 
                                        ? diff.changes.changes_summary.join('\n')
                                        : diff.changes.changes_summary}
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <span style={{
                                    backgroundColor: `${changeInfo.color}20`,
                                    color: changeInfo.color,
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                  }}>
                                    {changeInfo.label}
                                  </span>
                                  <button
                                    onClick={() => setSelectedDiff(diff)}
                                    className="btn btn-primary"
                                    style={{ padding: '4px 8px', fontSize: '12px' }}
                                  >
                                    Просмотр
                                  </button>
                                  <button
                                    onClick={() => confirmDeleteDiff(diff)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#ef4444',
                                      cursor: 'pointer',
                                      padding: '4px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                    title="Удалить это изменение"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Pagination - only for list view */}
            {viewMode === 'list' && totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Назад
                </button>
                <span style={{ padding: '0 16px' }}>
                  Страница {page} из {totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Вперёд
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Diff Details */}
        {selectedDiff && (
          <div style={{ width: '500px', position: 'sticky', top: '28px', maxHeight: 'calc(100vh - 56px)', overflow: 'auto' }}>
            <div className="card">
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1e293b',
                }}>
                  Детали изменения
                </h3>
                <button
                  onClick={() => setSelectedDiff(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6b7280',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <strong>URL:</strong>
                <div style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  padding: '8px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginTop: '4px',
                  wordBreak: 'break-all',
                }}>
                  {selectedDiff.host}
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <strong>Обнаружено:</strong>
                <div style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  padding: '8px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginTop: '4px',
                }}>
                  {formatDate(selectedDiff.detected_at)}
                </div>
              </div>

              {(selectedDiff.previous_config_id || selectedDiff.new_config_id) && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>Сравнение конфигов:</strong>
                  <div style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    padding: '8px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginTop: '4px',
                  }}>
                    {selectedDiff.previous_config_id && (
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Из:</span> конфиг #{selectedDiff.previous_config_id.slice(-8)}
                      </div>
                    )}
                    {selectedDiff.new_config_id && (
                      <div>
                        <span style={{ color: 'var(--text-secondary)' }}>В:</span> конфиг #{selectedDiff.new_config_id.slice(-8)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <strong>Сводка изменений:</strong>
                <div style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  padding: '8px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginTop: '4px',
                  whiteSpace: 'pre-line',
                }}>
                  {selectedDiff.changes.changes_summary 
                    ? (Array.isArray(selectedDiff.changes.changes_summary) 
                        ? selectedDiff.changes.changes_summary.join('\n')
                        : selectedDiff.changes.changes_summary)
                    : 'Подробные изменения отсутствуют'}
                </div>
              </div>

              {/* Specific Changes */}
              {(selectedDiff.changes.added_tech?.length > 0 || 
                selectedDiff.changes.removed_tech?.length > 0 ||
                selectedDiff.changes.added_links?.length > 0 ||
                selectedDiff.changes.removed_links?.length > 0 ||
                selectedDiff.changes.modified_links?.length > 0 ||
                selectedDiff.changes.added_relations?.length > 0 ||
                selectedDiff.changes.removed_relations?.length > 0 ||
                selectedDiff.changes.modify_changes) && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>Подробные изменения:</strong>
                  <div style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    padding: '8px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    marginTop: '4px',
                  }}>
                    {selectedDiff.changes.added_tech?.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#10b981', fontWeight: '500' }}>
                          Добавленные технологии:
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                          {selectedDiff.changes.added_tech.map((tech, index) => (
                            <span
                              key={index}
                              style={{
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                marginRight: '4px',
                                marginBottom: '2px',
                                display: 'inline-block',
                              }}
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedDiff.changes.removed_tech?.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#ef4444', fontWeight: '500' }}>
                          Удалённые технологии:
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                          {selectedDiff.changes.removed_tech.map((tech, index) => (
                            <span
                              key={index}
                              style={{
                                backgroundColor: '#fef2f2',
                                color: '#991b1b',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                marginRight: '4px',
                                marginBottom: '2px',
                                display: 'inline-block',
                              }}
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedDiff.changes.added_links?.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#10b981', fontWeight: '500' }}>
                          Добавленные локации ({selectedDiff.changes.added_links.length}):
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px', maxHeight: '100px', overflowY: 'auto' }}>
                          {selectedDiff.changes.added_links.map((link, index) => (
                            <div
                              key={index}
                              style={{
                                fontSize: '12px',
                                color: '#166534',
                                marginBottom: '2px',
                                wordBreak: 'break-all',
                                padding: '4px',
                                backgroundColor: '#f0fdf4',
                                borderRadius: '4px',
                                border: '1px solid #bbf7d0',
                              }}
                            >
                              <div style={{ fontWeight: '500' }}>
                                + {typeof link === 'string' ? link : link.url}
                              </div>
                              {typeof link === 'object' && (
                                <div style={{ marginTop: '2px', fontSize: '11px', color: '#15803d' }}>
                                  {link.status_code && <span>Статус: {link.status_code} | </span>}
                                  {link.size && <span>Размер: {link.size} байт | </span>}
                                  {link.ip && <span>IP: {link.ip} | </span>}
                                  {link.tech && link.tech.length > 0 && (
                                    <span>Технологии: {Array.isArray(link.tech) ? link.tech.join(', ') : link.tech}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedDiff.changes.removed_links?.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#ef4444', fontWeight: '500' }}>
                          Удалённые локации ({selectedDiff.changes.removed_links.length}):
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px', maxHeight: '100px', overflowY: 'auto' }}>
                          {selectedDiff.changes.removed_links.map((link, index) => (
                            <div
                              key={index}
                              style={{
                                fontSize: '12px',
                                color: '#991b1b',
                                marginBottom: '2px',
                                wordBreak: 'break-all',
                                padding: '4px',
                                backgroundColor: '#fef2f2',
                                borderRadius: '4px',
                                border: '1px solid #fecaca',
                              }}
                            >
                              <div style={{ fontWeight: '500' }}>
                                - {typeof link === 'string' ? link : link.url}
                              </div>
                              {typeof link === 'object' && (
                                <div style={{ marginTop: '2px', fontSize: '11px', color: '#dc2626' }}>
                                  {link.status_code && <span>Статус: {link.status_code} | </span>}
                                  {link.size && <span>Размер: {link.size} байт | </span>}
                                  {link.ip && <span>IP: {link.ip} | </span>}
                                  {link.tech && link.tech.length > 0 && (
                                    <span>Технологии: {Array.isArray(link.tech) ? link.tech.join(', ') : link.tech}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Modify Changes */}
                    {selectedDiff.changes.modify_changes && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#3b82f6', fontWeight: '500' }}>
                          Изменения modify:
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                          {Object.entries(selectedDiff.changes.modify_changes.new || {}).map(([key, newValue]) => {
                            const oldValue = selectedDiff.changes.modify_changes.old?.[key];
                            if (oldValue !== newValue) {
                              return (
                                <div
                                  key={key}
                                  style={{
                                    fontSize: '12px',
                                    color: '#1e40af',
                                    marginBottom: '2px',
                                    padding: '4px',
                                    backgroundColor: '#eff6ff',
                                    borderRadius: '4px',
                                    border: '1px solid #bfdbfe',
                                  }}
                                >
                                  <div style={{ fontWeight: '500' }}>
                                    {key}: {oldValue || 'null'} → {newValue || 'null'}
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Added Relations */}
                    {selectedDiff.changes.added_relations?.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#8b5cf6', fontWeight: '500' }}>
                          Добавленные связи ({selectedDiff.changes.added_relations.length}):
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px', maxHeight: '100px', overflowY: 'auto' }}>
                          {selectedDiff.changes.added_relations.map((relation, index) => (
                            <div
                              key={index}
                              style={{
                                fontSize: '12px',
                                color: '#6b21a8',
                                marginBottom: '2px',
                                wordBreak: 'break-all',
                                padding: '4px',
                                backgroundColor: '#faf5ff',
                                borderRadius: '4px',
                                border: '1px solid #d8b4fe',
                              }}
                            >
                              <div style={{ fontWeight: '500' }}>
                                + {relation}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Removed Relations */}
                    {selectedDiff.changes.removed_relations?.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#ef4444', fontWeight: '500' }}>
                          Удалённые связи ({selectedDiff.changes.removed_relations.length}):
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px', maxHeight: '100px', overflowY: 'auto' }}>
                          {selectedDiff.changes.removed_relations.map((relation, index) => (
                            <div
                              key={index}
                              style={{
                                fontSize: '12px',
                                color: '#991b1b',
                                marginBottom: '2px',
                                wordBreak: 'break-all',
                                padding: '4px',
                                backgroundColor: '#fef2f2',
                                borderRadius: '4px',
                                border: '1px solid #fecaca',
                              }}
                            >
                              <div style={{ fontWeight: '500' }}>
                                - {relation}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Modified Links */}
                    {selectedDiff.changes.modified_links?.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <span style={{ color: '#f59e0b', fontWeight: '500' }}>
                          Изменённые локации ({selectedDiff.changes.modified_links.length}):
                        </span>
                        <div style={{ marginLeft: '16px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                          {selectedDiff.changes.modified_links.map((linkChange, index) => (
                            <div
                              key={index}
                              style={{
                                fontSize: '12px',
                                color: '#92400e',
                                marginBottom: '8px',
                                padding: '8px',
                                backgroundColor: '#fffbeb',
                                borderRadius: '4px',
                                border: '1px solid #fed7aa',
                              }}
                            >
                              <div style={{ fontWeight: '500', marginBottom: '4px', wordBreak: 'break-all' }}>
                                {linkChange.url}
                              </div>
                              {Object.entries(linkChange.field_changes || {}).map(([field, change]) => {
                                // Debug logging
                                if (field === 'tech') {
                                  console.log('Отладка изменения технологий:', {
                                    field,
                                    old: change.old,
                                    new: change.new,
                                    oldType: typeof change.old,
                                    newType: typeof change.new,
                                    oldIsArray: Array.isArray(change.old),
                                    newIsArray: Array.isArray(change.new)
                                  });
                                }

                                // Special handling for tech arrays and IP
                                const formatValue = (value) => {
                                  if (field === 'tech' && Array.isArray(value)) {
                                    return value.length > 0 ? value.join(', ') : 'нет';
                                  }
                                  return value;
                                };

                                const formatDiff = (oldVal, newVal) => {
                                  if (field === 'tech') {
                                    const formatTechArray = (val) => {
                                      if (Array.isArray(val)) {
                                        return val.length > 0 ? val.join(', ') : 'нет';
                                      }
                                      return val || 'нет';
                                    };
                                    return `${formatTechArray(oldVal)} → ${formatTechArray(newVal)}`;
                                  }
                                  return `${change.old} → ${change.new}`;
                                };

                                return (
                                  <div
                                    key={field}
                                    style={{
                                      fontSize: '11px',
                                      marginBottom: '2px',
                                      padding: '2px 4px',
                                      backgroundColor: field === 'ip' ? '#fef2f2' : '#fef3c7',
                                      color: field === 'ip' ? '#991b1b' : '#92400e',
                                      borderRadius: '3px',
                                    }}
                                  >
                                    <span style={{ fontWeight: '500' }}>{field}:</span> {formatDiff(change.old, change.new)}
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={showClearDialog}
        onClose={() => setShowClearDialog(false)}
        onConfirm={handleClearAllDiffs}
        title="Очистить все различия"
        message={`Вы уверены, что хотите удалить все различия (${total})? Это действие необратимо.`}
        confirmText={deleting ? 'Очистка...' : 'Очистить всё'}
        cancelText="Отмена"
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => handleDeleteDiff(diffToDelete?._id)}
        title="Удалить различие"
        message={`Вы уверены, что хотите удалить различие для "${diffToDelete?.host}"? Это действие необратимо.`}
        confirmText={deleting ? 'Удаление...' : 'Удалить'}
        cancelText="Отмена"
      />
    </div>
  );
};

export default Diff;
